import { useAuthStore } from '@/stores/auth'
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

// Route definitions
const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/HomeView.vue'),
    meta: {
      title: 'Beer Rating App',
    },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: {
      title: 'Login',
      requiresGuest: true, // Redirect to home if already logged in
    },
  },
  {
    path: '/register',
    name: 'registration',
    component: () => import('@/views/RegistrationView.vue'),
    meta: {
      title: 'Register',
      requiresGuest: true, // Redirect to home if already logged in
    },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/views/PasswordResetView.vue'),
    meta: {
      title: 'Reset Password',
      requiresGuest: true, // Redirect to home if already logged in
    },
  },
  {
    path: '/session-invite',
    name: 'session-invite',
    component: () => import('@/views/SessionInviteView.vue'),
    meta: {
      title: 'Join Session',
    },
  },
  {
    path: '/ratings',
    name: 'ratings',
    component: () => import('@/views/RatingsListView.vue'),
    meta: {
      title: 'Beer Ratings',
      requiresAuth: true,
    },
  },
  {
    path: '/my-statistics',
    name: 'my-statistics',
    component: () => import('@/views/MyStatisticsView.vue'),
    meta: {
      title: 'My Statistics',
      requiresAuth: true,
    },
  },
  {
    path: '/ratings/new',
    name: 'new-rating',
    component: () => import('@/views/NewRatingView.vue'),
    meta: {
      title: 'New Beer Rating',
      requiresAuth: true,
    },
  },
  {
    path: '/ratings/:guid',
    name: 'edit-rating',
    component: () => import('@/views/EditRatingView.vue'),
    meta: {
      title: 'Edit Rating',
      requiresAuth: true,
    },
    props: true, // Pass route params as props
  },
  {
    path: '/ratings/:guid/view',
    name: 'rating-detail',
    component: () => import('@/views/RatingDetailView.vue'),
    meta: {
      title: 'Rating Details',
      requiresAuth: true,
    },
    props: true,
  },
  {
    path: '/ratings/:guid/pdf',
    name: 'rating-pdf',
    component: () => import('@/views/RatingPdfView.vue'),
    meta: {
      title: 'Rating PDF',
      requiresAuth: true,
    },
    props: true,
  },
  {
    path: '/beers/:guid',
    name: 'beer-detail',
    component: () => import('@/views/BeerDetailView.vue'),
    meta: {
      title: 'Beer Details',
      requiresAuth: true,
    },
    props: true,
  },
  {
    path: '/breweries/:guid',
    name: 'brewery-detail',
    component: () => import('@/views/BreweryDetailView.vue'),
    meta: {
      title: 'Brewery Details',
      requiresAuth: true,
    },
    props: true,
  },
  {
    path: '/pubs',
    name: 'pubs',
    component: () => import('@/views/PubsView.vue'),
    meta: {
      title: 'Pubs Overview',
      requiresAuth: true,
    },
  },
  {
    path: '/pubs/:guid',
    name: 'pub-detail',
    component: () => import('@/views/PubDetailView.vue'),
    meta: {
      title: 'Pub Details',
      requiresAuth: true,
    },
    props: true,
  },
  {
    path: '/breweries',
    name: 'breweries',
    component: () => import('@/views/BreweriesView.vue'),
    meta: {
      title: 'Breweries Overview',
      requiresAuth: true,
    },
  },
  {
    path: '/events/:guid',
    name: 'event-detail',
    component: () => import('@/views/EventDetailView.vue'),
    meta: {
      title: 'Event Details',
      requiresAuth: true,
    },
    props: true,
  },
  {
    path: '/session-entry',
    name: 'session-entry',
    component: () => import('@/views/SessionEntryView.vue'),
    meta: {
      title: 'Start Rating Session',
      requiresAuth: true,
    },
  },
  {
    path: '/sessions',
    name: 'sessions',
    component: () => import('@/views/SessionsView.vue'),
    meta: {
      title: 'Beer Tasting Sessions',
      requiresAuth: true,
    },
  },
  {
    path: '/sessions/new',
    name: 'session-create',
    component: () => import('@/views/SessionCreateView.vue'),
    meta: {
      title: 'Create New Session',
      requiresAuth: true,
    },
  },
  {
    path: '/sessions/:guid',
    name: 'session-detail',
    component: () => import('@/views/SessionDetailView.vue'),
    meta: {
      title: 'Session Details',
      requiresAuth: true,
    },
    props: true,
  },
  {
    path: '/sessions/:guid/settings',
    name: 'session-settings',
    component: () => import('@/views/SessionSettingsView.vue'),
    meta: {
      title: 'Session Settings',
      requiresAuth: true,
    },
    props: true,
  },
  {
    path: '/sessions/:guid/print',
    name: 'session-print',
    component: () => import('@/views/SessionPrintView.vue'),
    meta: {
      title: 'Print Session Ratings',
      requiresAuth: true,
    },
    props: true,
  },
  {
    path: '/sessions/:guid/blind-tasting-setup',
    name: 'blind-tasting-setup',
    component: () => import('@/views/BlindTastingSetupView.vue'),
    meta: {
      title: 'Blind Tasting Setup',
      requiresAuth: true,
    },
    props: true,
  },
  {
    path: '/blind-tasting/:token',
    name: 'blind-tasting-label',
    component: () => import('@/views/BlindTastingLabelView.vue'),
    meta: {
      title: 'Label Glasses for Blind Tasting',
    },
    props: true,
  },
  {
    path: '/users',
    name: 'users',
    component: () => import('@/views/UsersView.vue'),
    meta: {
      title: 'User Management',
      requiresAuth: true,
      requiresAdmin: true,
    },
  },
  {
    path: '/users/new',
    name: 'new-user',
    component: () => import('@/views/NewUserView.vue'),
    meta: {
      title: 'New User',
      requiresAuth: true,
      requiresAdmin: true,
    },
  },
  {
    path: '/users/:guid',
    name: 'edit-user',
    component: () => import('@/views/EditUserView.vue'),
    meta: {
      title: 'Edit User',
      requiresAuth: true,
      requiresAdmin: true,
    },
    props: true, // Pass route params as props
  },
  {
    path: '/administration',
    name: 'administration',
    component: () => import('@/views/AdministrationView.vue'),
    meta: {
      title: 'Administration',
      requiresAuth: true,
      requiresAdmin: true,
    },
  },
  {
    path: '/system-settings',
    name: 'system-settings',
    component: () => import('@/views/SystemSettingsView.vue'),
    meta: {
      title: 'System Settings',
      requiresAuth: true,
      requiresAdmin: true,
    },
  },
  {
    path: '/system-statistics',
    name: 'system-statistics',
    component: () => import('@/views/SystemStatisticsView.vue'),
    meta: {
      title: 'System Statistics',
      requiresAuth: true,
      requiresAdmin: true,
    },
  },
  {
    path: '/administration/style-migration',
    name: 'style-migration',
    component: () => import('@/views/StyleMigrationView.vue'),
    meta: {
      title: 'Beer Style Migration',
      requiresAuth: true,
      requiresAdmin: true,
    },
  },
  {
    path: '/styles',
    name: 'styles',
    component: () => import('@/views/StylesListingView.vue'),
    meta: {
      title: 'Beer Styles',
      requiresAuth: true,
    },
  },
  {
    path: '/styles/bjcp/:id',
    name: 'bjcp-style-detail',
    component: () => import('@/views/StyleDetailView.vue'),
    meta: {
      title: 'Style Details',
      requiresAuth: true,
    },
    props: (r) => ({ styleId: r.params.id, isCustom: false }),
  },
  {
    path: '/styles/custom/:id',
    name: 'custom-style-detail',
    component: () => import('@/views/StyleDetailView.vue'),
    meta: {
      title: 'Style Details',
      requiresAuth: true,
    },
    props: (r) => ({ styleId: r.params.id, isCustom: true }),
  },
  {
    path: '/my-tastings',
    name: 'simple-ratings',
    component: () => import('@/views/SimpleRatingsView.vue'),
    meta: { title: 'My Tastings', requiresAuth: true },
  },
  {
    path: '/my-tastings/new',
    name: 'simple-rating-guide',
    component: () => import('@/views/SimpleRatingGuideView.vue'),
    meta: { title: 'New Tasting', requiresAuth: true },
  },
  {
    path: '/my-tastings/edit/:guid',
    name: 'simple-rating-guide-edit',
    component: () => import('@/views/SimpleRatingGuideView.vue'),
    meta: { title: 'Edit Tasting', requiresAuth: true },
  },
  // 404 Not Found route
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { title: 'Page Not Found' },
  },
]

// Create router instance
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  // Scroll behavior for better UX
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  },
})

// Navigation guards
router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()

  // Set page title
  if (to.meta.title) {
    document.title = `${to.meta.title} - Beerview`
  }

  // Wait for auth initialization if not completed yet
  if (!authStore.isInitialized && !authStore.isInitializing) {
    await authStore.initializeAuth()
  }

  // If still initializing, wait for completion
  while (authStore.isInitializing) {
    await new Promise(resolve => setTimeout(resolve, 10))
  }

  // Check authentication requirements
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    // Redirect to login with return path
    next({
      name: 'login',
      query: { redirect: to.fullPath },
    })
    return
  }

  // Check admin requirements
  if (to.meta.requiresAdmin && !authStore.user?.isAdmin) {
    // Redirect to 404 page if not admin
    next({ name: 'not-found' })
    return
  }

  // Redirect authenticated users away from login page
  if (to.meta.requiresGuest && authStore.isAuthenticated) {
    next({ name: 'home' })
    return
  }

  // Allow navigation
  next()
})

// Handle navigation errors gracefully
router.onError(error => {
  console.error('Router error:', error)
})

export default router
